import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, ArrowRight, Heart, Sparkle } from "@phosphor-icons/react";
import { SiteNav } from "../components/SiteNav";
import { ForgetMeNotIcon } from "../components/ForgetMeNotIcon";
import { useAuth } from "../lib/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function Dashboard() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [profiles, setProfiles] = useState([]);
    const [loadingProfiles, setLoadingProfiles] = useState(true);
    const [seeding, setSeeding] = useState(false);

    useEffect(() => {
        if (!loading && (!user || user === false)) navigate("/sign-in");
    }, [user, loading, navigate]);

    useEffect(() => {
        if (!user || user === false) return;
        let active = true;
        (async () => {
            try {
                const { data } = await api.get("/profiles");
                if (active) setProfiles(data.profiles || []);
            } finally {
                if (active) setLoadingProfiles(false);
            }
        })();
        return () => { active = false; };
    }, [user]);

    if (loading || !user || user === false) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]" data-testid="dashboard-loading">
                <ForgetMeNotIcon size={48} className="animate-pulse" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FDFBF7]" data-testid="dashboard-page">
            <SiteNav />
            <main className="pt-28 pb-24 px-6 rem-watercolor-bg min-h-screen">
                <div className="max-w-6xl mx-auto">
                    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-3">
                            Good morning, {user.name || user.email.split("@")[0]}
                        </p>
                        <h1 className="font-serif text-4xl sm:text-5xl text-[#2D2B2A] leading-tight" data-testid="dashboard-heading">
                            Your family's <em className="italic text-[#4A5A95]">remembrs</em>
                        </h1>
                        <p className="mt-4 text-[#5C5A59] max-w-2xl">
                            Every profile here is a lifetime, gathered together by the people who love them.
                        </p>

                        {profiles.length === 0 && !loadingProfiles && (
                            <button
                                onClick={async () => {
                                    if (seeding) return;
                                    setSeeding(true);
                                    try {
                                        // Step 1: Fast seed (photos + memories, no audio yet) ~10s
                                        const { data: seed } = await api.post("/profiles/seed-john-tilly", null, { timeout: 60000 });
                                        toast.success(`John Tilly's remembr built — ${seed.memories_created} memories with photos. Now adding voices…`);
                                        const profileId = seed.profile_id;

                                        // Refresh dashboard so the card shows immediately
                                        const { data: list } = await api.get("/profiles");
                                        setProfiles(list.profiles || []);

                                        // Step 2: Generate narrations one at a time (each ~6s — fits ingress)
                                        let done = 0;
                                        const total = seed.memories_created;
                                        // Loop until no more pending
                                        // Show toast progress every 5 memories
                                        // eslint-disable-next-line no-constant-condition
                                        while (true) {
                                            const { data: status } = await api.get(`/profiles/${profileId}/narration-status`);
                                            if (!status.next_pending_id) break;
                                            await api.post(`/memories/${status.next_pending_id}/generate-narration`, null, { timeout: 45000 });
                                            done = (status.with_audio || 0) + 1;
                                            if (done % 5 === 0 || done === total) {
                                                toast.info(`Voice narrations: ${done}/${total} ready…`, { duration: 2000 });
                                            }
                                        }
                                        toast.success(`All voice narrations ready. Open remembr Mode to play John's story.`, { duration: 6000 });
                                    } catch (err) {
                                        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not seed demo.");
                                    } finally {
                                        setSeeding(false);
                                    }
                                }}
                                disabled={seeding}
                                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F4D068] hover:bg-[#E6B844] text-[#2D2B2A] text-sm font-medium transition-all disabled:opacity-60"
                                data-testid="seed-john-tilly-btn"
                            >
                                <Sparkle size={16} weight="fill" />
                                {seeding ? "Building John's story…" : "Try the John Tilly demo (1-click)"}
                            </button>
                        )}
                    </motion.div>

                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <Link
                            to="/profiles/new"
                            className="group rounded-3xl border-2 border-dashed border-[#8C9ECB]/40 bg-white/60 hover:bg-white hover:border-[#6B7DB8] p-8 min-h-[260px] flex flex-col items-center justify-center text-center transition-all hover:-translate-y-1"
                            data-testid="create-profile-card"
                        >
                            <div className="w-14 h-14 rounded-full bg-[#F4F6FB] flex items-center justify-center mb-4 group-hover:bg-[#8C9ECB] group-hover:text-white transition-colors">
                                <Plus size={28} weight="light" />
                            </div>
                            <h3 className="font-serif text-xl text-[#2D2B2A]">Start a new remembr</h3>
                            <p className="mt-2 text-sm text-[#5C5A59]">For one loved one — or for a couple, together.</p>
                        </Link>

                        {loadingProfiles ? (
                            <div className="rounded-3xl bg-white/60 p-8 min-h-[260px] flex items-center justify-center" data-testid="profiles-loading">
                                <ForgetMeNotIcon size={32} className="animate-pulse opacity-60" />
                            </div>
                        ) : (
                            profiles.map((p, i) => (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 18 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: i * 0.05 }}
                                    data-testid={`profile-card-${p.id}`}
                                >
                                    <Link
                                        to={`/profiles/${p.id}`}
                                        className="block group bg-white rounded-3xl border border-[#E5E1D8]/70 hover:shadow-rem-card transition-all hover:-translate-y-1 p-8 min-h-[260px]"
                                    >
                                        <div className="flex items-center gap-2 mb-4">
                                            <ForgetMeNotIcon size={18} />
                                            <p className="text-[11px] uppercase tracking-[0.22em] text-[#6B7DB8] font-semibold">
                                                {p.is_couple ? "Couples profile" : p.relationship || "Profile"}
                                            </p>
                                        </div>
                                        <h3 className="font-serif text-2xl text-[#2D2B2A] leading-tight">
                                            {p.subject_name}
                                            {p.subject_name_2 && (<><span className="text-[#6B7DB8] italic"> & </span>{p.subject_name_2}</>)}
                                        </h3>
                                        {p.birth_year && (
                                            <p className="mt-2 text-sm text-[#8A8786]">
                                                {p.birth_place ? `Born ${p.birth_place} · ${p.birth_year}` : `Born ${p.birth_year}`}
                                            </p>
                                        )}
                                        <div className="mt-6 flex items-center justify-between text-sm">
                                            <span className="text-[#5C5A59]">
                                                {p.memory_count || 0} {(p.memory_count || 0) === 1 ? "memory" : "memories"}
                                            </span>
                                            <span className="inline-flex items-center gap-1 text-[#6B7DB8] group-hover:text-[#4A5A95] font-medium">
                                                Open <ArrowRight size={14} weight="bold" />
                                            </span>
                                        </div>
                                    </Link>
                                </motion.div>
                            ))
                        )}
                    </div>

                    {!loadingProfiles && profiles.length === 0 && (
                        <div className="mt-12 max-w-2xl mx-auto text-center rounded-3xl bg-white p-10 border border-[#E5E1D8]/60" data-testid="empty-state">
                            <div className="flex justify-center mb-4"><Heart size={32} weight="light" color="#8C9ECB" /></div>
                            <h2 className="font-serif text-2xl text-[#2D2B2A]">Your first remembr</h2>
                            <p className="mt-3 text-[#5C5A59] leading-relaxed">
                                Begin with one loved one. You'll build it together with the rest of the family — photos,
                                voices, songs, the stories worth keeping.
                            </p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
